import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

type DocumentTitleOverrideContextValue = {
  override: string | undefined;
  setOverride: (pageName: string | undefined) => void;
};

const DocumentTitleOverrideContext = createContext<DocumentTitleOverrideContextValue | null>(null);

export const DocumentTitleOverrideProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [override, setOverrideState] = useState<string | undefined>();

  const setOverride = useCallback((pageName: string | undefined) => {
    setOverrideState(pageName);
  }, []);

  const value = useMemo(
    () => ({
      override,
      setOverride
    }),
    [override, setOverride]
  );

  return (
    <DocumentTitleOverrideContext.Provider value={value}>
      {children}
    </DocumentTitleOverrideContext.Provider>
  );
};

export const useDocumentTitleOverrideValue = (): string | undefined => {
  return useContext(DocumentTitleOverrideContext)?.override;
};

/**
 * Sets a dynamic document title page-name (before `| io.net`) while mounted.
 * Prefer this on detail pages once the viewed resource name is known.
 */
export const useDocumentTitleOverride = (pageName: string | undefined) => {
  const ctx = useContext(DocumentTitleOverrideContext);

  useEffect(() => {
    if (!ctx) {
      return;
    }
    ctx.setOverride(pageName);
    return () => {
      ctx.setOverride(undefined);
    };
  }, [ctx, pageName]);
};
